import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import DataDisclosure from '../Inventory/DataDisclosure';
import { fmtMoney, fmtDate, fmtCyds, fmtRate, fmtPct, costCenterName } from './format';
import { c, chart } from '../../theme';

// All four measures, over every committed export, for whichever cost center is
// selected on the board -- one click re-reads the whole trend for that yard.
//
// Four charts rather than one with a metric switch: the questions are asked
// together. A yard whose Rev$/Cyd is falling while its volume climbs is a
// different story from one where both fall, and a switch makes the reader hold
// the first chart in their head while looking at the second.
//
// Each chart carries its own scale, because a dollar rate, a revenue total, a
// volume and a percentage share nothing. The y-axis shows both ends of what
// that chart actually covers, so a line that looks dramatic can be read as the
// small movement it usually is.
const METRICS = [
  { key: 'rev_per_cyd',        label: 'Rev$/Cyd',      fmt: fmtRate,            axis: (v) => `$${Number(v).toFixed(2)}` },
  { key: 'revenue',            label: 'Revenue',       fmt: fmtMoney,           axis: (v) => compactMoney(v) },
  { key: 'volume',             label: 'Monthly Cyds',  fmt: fmtCyds,            axis: (v) => compactNumber(v) },
  { key: 'participation_rate', label: 'Participation', fmt: (v) => fmtPct(v, 1), axis: (v) => fmtPct(v) },
];

// Axis labels only: a tick reading $6,343,128 crowds out the chart it belongs to.
function compactMoney(v) {
  const n = Number(v) || 0;
  if (Math.abs(n) >= 1000000) return `$${(n / 1000000).toFixed(2)}M`;
  if (Math.abs(n) >= 1000) return `$${Math.round(n / 1000)}K`;
  return `$${Math.round(n)}`;
}
function compactNumber(v) {
  const n = Number(v) || 0;
  return n >= 10000 ? `${Math.round(n / 1000)}K` : Math.round(n).toLocaleString('en-US');
}

export default function SalesTrend({ periods, series, selected, onClear }) {
  if (!periods.length) {
    return <div style={s.empty}>No committed exports yet — the trend builds as each export is loaded.</div>;
  }

  const chosen = selected ? series.find((sr) => sr.cost_center === selected) : null;
  // A cost center with no lines in any committed export would leave the charts
  // empty; say so rather than draw four flat lines through nothing.
  const points = chosen ? chosen.points : periods;
  const scopeLabel = selected ? costCenterName(selected) : 'All cost centers';

  return (
    <div>
      <div style={s.head}>
        <div style={s.scope}>
          Showing <strong style={s.scopeName}>{scopeLabel}</strong>
          <span style={s.hint}>
            {selected ? ' — click the row again, or Back to all' : ' — click a cost center above to follow it here'}
          </span>
        </div>
        {selected && (
          <button type="button" onClick={onClear} style={s.clear}>Back to all</button>
        )}
      </div>

      {points.length ? (
        <div style={s.grid}>
          {METRICS.map((m) => <MiniChart key={m.key} metric={m} points={points} />)}
        </div>
      ) : (
        <div style={s.empty}>{scopeLabel} has no lines in any committed export.</div>
      )}

      <DataDisclosure label="cost center" count={series.length}>
        <div style={{ overflowX: 'auto' }}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Cost Center</th>
                {periods.slice(-8).map((p) => <th key={p.batch_id} style={s.thR}>{fmtDate(p.as_of_date)}</th>)}
              </tr>
            </thead>
            <tbody>
              {series.map((sr) => {
                const byBatch = new Map(sr.points.map((p) => [p.batch_id, p]));
                return (
                  <tr key={sr.cost_center ?? 'unresolved'} style={s.tr}>
                    <td style={s.td}>{costCenterName(sr.cost_center)}</td>
                    {/* An export this cost center has no lines in is an em dash,
                        not a zero: absent is not the same as nothing. */}
                    {periods.slice(-8).map((p) => (
                      <td key={p.batch_id} style={s.tdR}>
                        {byBatch.has(p.batch_id) ? fmtRate(byBatch.get(p.batch_id).rev_per_cyd) : '—'}
                      </td>
                    ))}
                  </tr>
                );
              })}
              <tr style={s.tr}>
                <td style={{ ...s.td, fontWeight: 700 }}>All cost centers</td>
                {periods.slice(-8).map((p) => (
                  <td key={p.batch_id} style={{ ...s.tdR, fontWeight: 700 }}>{fmtRate(p.rev_per_cyd)}</td>
                ))}
              </tr>
            </tbody>
          </table>
          <div style={s.tableNote}>Rev$/Cyd, last {Math.min(periods.length, 8)} exports.</div>
        </div>
      </DataDisclosure>
    </div>
  );
}

function MiniChart({ metric, points }) {
  const data = points.map((p) => ({ ...p, label: fmtDate(p.as_of_date) }));
  const latest = data[data.length - 1];
  const prior  = data.length > 1 ? data[data.length - 2] : null;
  const value  = latest?.[metric.key];
  const before = prior?.[metric.key];

  return (
    <div style={s.card}>
      <div style={s.cardHead}>
        <span style={s.cardLabel}>{metric.label}</span>
        <Delta current={value} previous={before} />
      </div>
      <div style={s.cardValue}>{value === null || value === undefined ? '—' : metric.fmt(value)}</div>
      <div style={s.chartWrap}>
        <ResponsiveContainer width="100%" height={92}>
          <LineChart data={data} margin={{ top: 6, right: 10, bottom: 0, left: 0 }}>
            <CartesianGrid vertical={false} stroke={chart.grid} />
            <XAxis dataKey="label" hide />
            <YAxis
              domain={['auto', 'auto']} tickFormatter={metric.axis} tickCount={2}
              tick={{ fontSize: 10, fill: chart.axisText }} axisLine={false} tickLine={false} width={56}
            />
            <Tooltip
              formatter={(v) => [metric.fmt(v), metric.label]}
              contentStyle={{ fontSize: '12px', borderRadius: '8px', border: `1px solid ${chart.tooltipBorder}` }}
            />
            <Line
              type="linear" dataKey={metric.key} stroke={chart.emphasis} strokeWidth={1.8}
              dot={{ r: 2.5, fill: chart.emphasis }} activeDot={{ r: 4 }}
              isAnimationActive={false} connectNulls={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div style={s.cardFoot}>
        <span>{data[0]?.label}</span>
        <span>{latest?.label}</span>
      </div>
    </div>
  );
}

// Movement against the export before this one. No direction is called good:
// there is no agreed Rev$/Cyd target yet, so the arrow states what happened and
// leaves the verdict to the reader.
function Delta({ current, previous }) {
  if (previous === null || previous === undefined || !previous) return null;
  if (current === null || current === undefined) return null;
  const pct = ((current - previous) / Math.abs(previous)) * 100;
  if (Math.abs(pct) < 0.05) return <span style={s.deltaFlat}>no change</span>;
  return (
    <span style={s.delta}>{pct > 0 ? '▲' : '▼'} {Math.abs(pct).toFixed(1)}%</span>
  );
}

const s = {
  head:      { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', padding: '0 16px 10px' },
  scope:     { fontSize: '11.5px', color: c.textMuted },
  scopeName: { color: c.text, fontWeight: 600 },
  hint:      { color: c.textFaint },
  clear:     { height: '28px', fontSize: '11px', fontFamily: 'inherit', marginLeft: 'auto', padding: '0 10px', borderRadius: '6px', borderWidth: '1px', borderStyle: 'solid', borderColor: c.border, background: c.surface, color: c.textMuted, cursor: 'pointer' },
  grid:      { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(236px, 1fr))', gap: '1px', background: c.border, borderTop: `1px solid ${c.border}`, borderBottom: `1px solid ${c.border}` },
  card:      { background: c.surface, padding: '12px 14px 10px', display: 'grid', gap: '3px', minWidth: 0 },
  cardHead:  { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px' },
  cardLabel: { fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: c.textMuted },
  cardValue: { fontSize: '18px', fontWeight: 700, color: c.text, lineHeight: 1.15, fontVariantNumeric: 'tabular-nums' },
  chartWrap: { marginTop: '4px' },
  cardFoot:  { display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: c.textFaint, fontVariantNumeric: 'tabular-nums' },
  delta:     { fontSize: '11px', fontWeight: 600, color: c.textMuted, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
  deltaFlat: { fontSize: '11px', color: c.textFaint, whiteSpace: 'nowrap' },
  empty:     { padding: '40px 16px', textAlign: 'center', color: c.textFaint, fontSize: '12.5px' },
  table:     { width: '100%', borderCollapse: 'collapse', background: c.surface, fontSize: '11.5px' },
  th:        { padding: '8px 10px', background: c.surfaceSunk, borderTop: `1px solid ${c.border}`, borderBottom: `1px solid ${c.border}`, fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: c.textMuted, whiteSpace: 'nowrap', textAlign: 'left' },
  thR:       { padding: '8px 10px', background: c.surfaceSunk, borderTop: `1px solid ${c.border}`, borderBottom: `1px solid ${c.border}`, fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: c.textMuted, whiteSpace: 'nowrap', textAlign: 'right' },
  tr:        { borderBottom: `1px solid ${c.canvas}` },
  td:        { padding: '7px 10px', color: c.textStrong, whiteSpace: 'nowrap' },
  tdR:       { padding: '7px 10px', color: c.textStrong, whiteSpace: 'nowrap', textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  tableNote: { fontSize: '10.5px', color: c.textFaint, padding: '8px 16px 12px' },
};
